// /admin/companies — FleetSpoke super admin: every account with its size; create an account and invite its
// first manager (invite-only onboarding, brief §5.6 / acceptance "creates a company end-to-end from /admin").
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { Loading, LoadError, Page } from '../../components/Page';
import {
  type CompanyOverview,
  inviteManager,
  type NewCompany,
  useCompanies,
  useCreateCompany,
  useSetCompanyActive,
} from '../../data/admin';

const TIMEZONES = ['Asia/Kuala_Lumpur', 'Asia/Singapore'];
export const blankManager = (): NewCompany['manager'] => ({
  full_name: '',
  phone: '',
  email: '',
  locale: 'en',
});
const codeFrom = (name: string) =>
  name
    .toUpperCase()
    .replace(/SDN\.?\s*BHD\.?|PTE\.?\s*LTD\.?|BERHAD|LTD\.?/g, '')
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 20);

export function useInviteError() {
  const { t } = useTranslation();
  return (code: string | null) =>
    code === null
      ? null
      : ((
          {
            member_of_another_account: t('settings.users.err_member_elsewhere'),
            super_admin: t('settings.users.err_super_admin'),
            already_super_admin: t('admin.super.err_already'),
            member_of_an_account: t('admin.super.err_member'),
            invalid_phone: t('settings.users.err_invalid_phone'),
            forbidden: t('common.not_allowed'),
          } as Record<string, string>
        )[code] ?? t('common.error_generic'));
}

export function ManagerFields({
  value,
  onChange,
  nameLabel,
}: {
  value: NewCompany['manager'];
  onChange(v: NewCompany['manager']): void;
  /** the name field's label (default "Manager name") */
  nameLabel?: string;
}) {
  const { t } = useTranslation();
  const set = (k: keyof NewCompany['manager']) => (e: { target: { value: string } }) =>
    onChange({ ...value, [k]: e.target.value });
  return (
    <Stack spacing={2}>
      <TextField
        label={nameLabel ?? t('admin.manager_name')}
        value={value.full_name}
        onChange={set('full_name')}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label={t('settings.users.phone')}
          value={value.phone}
          onChange={set('phone')}
          required
          helperText={t('settings.users.phone_help')}
          fullWidth
        />
        <TextField
          label={`${t('settings.users.email')} (${t('common.optional')})`}
          value={value.email}
          onChange={set('email')}
          type="email"
          fullWidth
        />
      </Stack>
      <TextField select label={t('common.language')} value={value.locale} onChange={set('locale')}>
        <MenuItem value="en">English</MenuItem>
        <MenuItem value="ms">Bahasa Melayu</MenuItem>
        <MenuItem value="zh">中文</MenuItem>
      </TextField>
    </Stack>
  );
}

function NewCompanyDialog({ onClose }: { onClose(): void }) {
  const { t } = useTranslation();
  const create = useCreateCompany();
  const inviteError = useInviteError();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [timezone, setTimezone] = useState(TIMEZONES[0]!);
  const [manager, setManager] = useState(blankManager);
  const done = create.data;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate({ name, code: codeTouched ? code : codeFrom(name), timezone, manager });
  };
  const failure =
    create.error && (create.error as { code?: string }).code === '23505'
      ? t('admin.code_taken')
      : create.error
        ? (create.error as Error).message
        : done
          ? inviteError(done.invited)
          : null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>{t('admin.new_company')}</DialogTitle>
        <DialogContent dividers>
          {done && !done.invited ? (
            <Alert severity="success">{t('admin.company_created')}</Alert>
          ) : (
            <Stack spacing={2}>
              <TextField
                label={t('admin.company_name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                disabled={!!done}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('admin.company_code')}
                  value={codeTouched ? code : codeFrom(name)}
                  onChange={(e) => {
                    setCodeTouched(true);
                    setCode(e.target.value.toUpperCase());
                  }}
                  helperText={t('admin.company_code_help')}
                  required
                  disabled={!!done}
                  slotProps={{ htmlInput: { pattern: '[A-Z0-9][A-Z0-9\\-]{1,19}' } }}
                  fullWidth
                />
                <TextField
                  select
                  label={t('admin.timezone')}
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  disabled={!!done}
                  sx={{ minWidth: 220 }}
                >
                  {TIMEZONES.map((tz) => (
                    <MenuItem key={tz} value={tz}>
                      {tz}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Typography variant="subtitle2">{t('admin.first_manager')}</Typography>
              <ManagerFields value={manager} onChange={setManager} />
            </Stack>
          )}
          {failure ? (
            <Alert severity={done ? 'warning' : 'error'} sx={{ mt: 2 }}>
              {done ? `${t('admin.company_created_invite_failed')} ${failure}` : failure}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{done ? t('common.close') : t('common.cancel')}</Button>
          {!done ? (
            <Button
              type="submit"
              variant="contained"
              disabled={create.isPending || !name.trim() || !manager.phone.trim()}
            >
              {t('admin.create_and_invite')}
            </Button>
          ) : null}
        </DialogActions>
      </form>
    </Dialog>
  );
}

export function InviteManagerDialog({
  company,
  onClose,
}: {
  company: CompanyOverview;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inviteError = useInviteError();
  const [manager, setManager] = useState(blankManager);
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState('busy');
    const code = await inviteManager(company.company_id, manager);
    setError(inviteError(code));
    setState(code ? 'idle' : 'done');
    if (!code) void queryClient.invalidateQueries({ queryKey: ['admin'] });
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={(e) => void submit(e)}>
        <DialogTitle>{t('admin.invite_manager_to', { company: company.name })}</DialogTitle>
        <DialogContent dividers>
          {state === 'done' ? (
            <Alert severity="success">{t('admin.manager_invited')}</Alert>
          ) : (
            <ManagerFields value={manager} onChange={setManager} />
          )}
          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
          {state !== 'done' ? (
            <Button
              type="submit"
              variant="contained"
              disabled={state === 'busy' || !manager.phone.trim()}
            >
              {t('admin.invite')}
            </Button>
          ) : null}
        </DialogActions>
      </form>
    </Dialog>
  );
}

export function AdminCompaniesPage() {
  const { t } = useTranslation();
  const companies = useCompanies();
  const setActive = useSetCompanyActive();
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState<CompanyOverview | null>(null);

  return (
    <Page
      title={t('admin.companies_title')}
      actions={
        <Button variant="contained" onClick={() => setCreating(true)}>
          {t('admin.new_company')}
        </Button>
      }
    >
      {companies.isLoading ? <Loading /> : null}
      {companies.error ? <LoadError error={companies.error} /> : null}
      {companies.data ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.company_name')}</TableCell>
                <TableCell>{t('admin.company_code')}</TableCell>
                <TableCell align="right">{t('admin.col_managers')}</TableCell>
                <TableCell align="right">{t('admin.col_members')}</TableCell>
                <TableCell align="right">{t('admin.col_sites')}</TableCell>
                <TableCell align="right">{t('admin.col_assets')}</TableCell>
                <TableCell align="right">{t('admin.col_tags')}</TableCell>
                <TableCell align="right">{t('admin.col_gateways')}</TableCell>
                <TableCell>{t('admin.col_active')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.data.map((c) => (
                <TableRow key={c.company_id} data-testid={`company-${c.code}`}>
                  <TableCell>
                    {c.kind === 'customer' ? (
                      <Link component={RouterLink} to={`/admin/companies/${c.company_id}`}>
                        {c.name}
                      </Link>
                    ) : (
                      c.name
                    )}{' '}
                    {c.kind === 'platform' ? (
                      <Chip size="small" label={t('admin.stock_account')} />
                    ) : null}
                  </TableCell>
                  <TableCell>{c.code}</TableCell>
                  <TableCell align="right">{c.managers}</TableCell>
                  <TableCell align="right">{c.members}</TableCell>
                  <TableCell align="right">{c.sites}</TableCell>
                  <TableCell align="right">{c.assets}</TableCell>
                  <TableCell align="right">{c.tags}</TableCell>
                  <TableCell align="right">
                    {c.gateways ? `${c.gateways_online} / ${c.gateways}` : '—'}
                  </TableCell>
                  <TableCell>
                    {c.kind === 'customer' ? (
                      <Switch
                        size="small"
                        checked={c.is_active}
                        onChange={(e) =>
                          setActive.mutate({ id: c.company_id, active: e.target.checked })
                        }
                        inputProps={{ 'aria-label': `${c.code} ${t('admin.col_active')}` }}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    {c.kind === 'customer' ? (
                      <Button size="small" onClick={() => setInviting(c)}>
                        {t('admin.invite_manager')}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      {creating ? <NewCompanyDialog onClose={() => setCreating(false)} /> : null}
      {inviting ? (
        <InviteManagerDialog company={inviting} onClose={() => setInviting(null)} />
      ) : null}
    </Page>
  );
}
